import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static targets = [
    'field',
    'fieldHint',
    'operatorSelect',
    'operatorSelectWrapper',
    'operatorWrapper',
    'staticLabel',
    'valueWrapper',
    'textInput',
    'listInput',
    'tagListInput',
    'enumInput',
    'textWrapper',
    'listWrapper',
    'listInputWrapper',
    'tagListInputWrapper',
    'enumWrapper',
    'unitPrefix',
    'unitSuffix',
    'hint',
    'warning',
  ]

  connect() {
    this.refresh()
    requestAnimationFrame(() => this.refresh())
  }

  update(event) {
    if (!event?.target) return
    if (this.element.dataset.operatorSyncing === '1') return

    this.refresh()
  }

  refresh() {
    const fieldChanged = this.resetIfFieldChanged()
    const config = this.fieldConfig()
    this.updateFieldHint(config)
    this.updateOperatorOptions(config)
    this.toggleValueInput(config, { fieldChanged })
    this.refreshConditionState(config)
  }

  updateFieldHint(config) {
    if (!this.hasFieldHintTarget) return

    const { fieldHint, fieldHintDocsUrl } = config
    const link = fieldHintDocsUrl
      ? ` <a href="${fieldHintDocsUrl}" target="_blank" rel="noopener" class="underline">Learn more</a>`
      : ''
    this.fieldHintTarget.innerHTML = fieldHint ? `${fieldHint}${link}` : ''
    this.fieldHintTarget.classList.toggle('hidden', !fieldHint)
  }

  refreshConditionState(config = this.fieldConfig()) {
    const field = this.fieldInput()?.value?.trim()
    const operator = this.hasOperatorSelectTarget ? this.operatorSelectTarget.value?.trim() : ''
    const value = this.normalizedValue()

    const hasValue = Array.isArray(value) ? value.length > 0 : Boolean(value)
    const requiresOperator = config.operatorSet && !['static', 'value_only'].includes(config.operatorSet)
    const requiresValue = config.valueType && config.operatorSet !== 'static'
    const hasOperator = Boolean(operator)
    const hasAnyValue = [field, operator, hasValue].some(Boolean)
    const complete = Boolean(field) && (!requiresOperator || hasOperator) && (!requiresValue || hasValue)

    this.element.classList.toggle('border-red-200', !complete && hasAnyValue)
    this.element.classList.toggle('bg-red-50', !complete && hasAnyValue)
    this.element.classList.toggle('bg-gray-50', !hasAnyValue || complete)
    if (this.hasWarningTarget) {
      this.warningTarget.classList.toggle('hidden', complete || !hasAnyValue)
    }
  }

  toggleValueInput(config, { fieldChanged = false } = {}) {
    const valueType = config.valueType
    const operatorSet = config.operatorSet
    const unit = config.unit
    const unitPosition = config.unitPosition || 'suffix'
    const enumValues = config.enumValues
    const defaultValue = config.defaultValue
    const textInput = this.hasTextInputTarget ? this.textInputTarget : null
    const listInput = this.hasListInputTarget ? this.listInputTarget : null
    const tagListInput = this.hasTagListInputTarget ? this.tagListInputTarget : null
    const enumInput = this.hasEnumInputTarget ? this.enumInputTarget : null
    const textWrapper = this.hasTextWrapperTarget ? this.textWrapperTarget : null
    const listWrapper = this.hasListWrapperTarget ? this.listWrapperTarget : null
    const listInputWrapper = this.hasListInputWrapperTarget ? this.listInputWrapperTarget : null
    const tagListInputWrapper = this.hasTagListInputWrapperTarget ? this.tagListInputWrapperTarget : null
    const enumWrapper = this.hasEnumWrapperTarget ? this.enumWrapperTarget : null
    const valueWrapper = this.hasValueWrapperTarget ? this.valueWrapperTarget : null
    const hintEl = this.hasHintTarget ? this.hintTarget : null
    const unitPrefix = this.hasUnitPrefixTarget ? this.unitPrefixTarget : null
    const unitSuffix = this.hasUnitSuffixTarget ? this.unitSuffixTarget : null
    const existingValue = fieldChanged ? undefined : this.normalizedValue()

    const hasConfig = Boolean(valueType || operatorSet)
    if (!hasConfig) {
      if (valueWrapper) {
        valueWrapper.classList.add('hidden')
      }
      if (hintEl) {
        hintEl.textContent = ''
        hintEl.classList.add('hidden')
      }
      if (textInput) {
        textInput.disabled = true
      }
      if (listInput) {
        listInput.disabled = true
      }
      if (tagListInput) {
        tagListInput.disabled = true
      }
      if (enumInput) {
        enumInput.disabled = true
      }
      if (textWrapper) {
        textWrapper.classList.add('hidden')
      }
      if (listWrapper) {
        listWrapper.classList.add('hidden')
      }
      if (listInputWrapper) {
        listInputWrapper.classList.remove('hidden')
      }
      if (tagListInputWrapper) {
        tagListInputWrapper.classList.add('hidden')
      }
      if (enumWrapper) {
        enumWrapper.classList.add('hidden')
      }
      return
    }

    const isStatic = operatorSet === 'static'
    const useList = valueType === 'list'
    const useEnum = valueType === 'enum' || valueType === 'boolean'
    const useText = !useList && !useEnum && !isStatic

    if (valueWrapper) {
      valueWrapper.classList.toggle('hidden', isStatic)
    }
    if (hintEl) {
      const showHint = Boolean(config.hint) && !isStatic
      hintEl.textContent = config.hint || ''
      hintEl.classList.toggle('hidden', !showHint)
    }
    const showUnit = unit && useText
    if (unitPrefix) {
      unitPrefix.textContent = unit || ''
      unitPrefix.classList.toggle('hidden', !(showUnit && unitPosition === 'prefix'))
    }
    if (unitSuffix) {
      unitSuffix.textContent = unit || ''
      unitSuffix.classList.toggle('hidden', !(showUnit && unitPosition !== 'prefix'))
    }

    if (textInput) {
      if (isStatic) {
        textInput.type = 'hidden'
        textInput.value = 'true'
        textInput.disabled = false
      } else {
        textInput.type = valueType === 'number' ? 'number' : 'text'
        textInput.disabled = !useText
        if (textInput.value === 'true' && textInput.type !== 'hidden') {
          textInput.value = ''
        }
        if (useText && existingValue && !Array.isArray(existingValue)) {
          textInput.value = existingValue
        }
        if (!useText && !isStatic) {
          textInput.value = ''
        }
      }
    }
    if (textWrapper) {
      textWrapper.classList.toggle('hidden', !useText)
      textWrapper.style.display = useText ? '' : 'none'
    }
    if (listInput) {
      const useResourceList = useList && Boolean(config.resourceListType)
      if (tagListInput) {
        this.updateResourceListInput(tagListInput, config.resourceListType)
      }
      const listValues = Array.isArray(existingValue) ? existingValue : existingValue ? [existingValue] : []
      const plainValues = useList && !useResourceList ? listValues : []
      const resourceValues = useResourceList ? listValues : []

      this.setListValues(listInput, plainValues, useList && !useResourceList)
      if (tagListInput) {
        this.setListValues(tagListInput, resourceValues, useResourceList)
      }

      if (listInputWrapper) {
        listInputWrapper.classList.toggle('hidden', useResourceList)
      }
      if (tagListInputWrapper) {
        tagListInputWrapper.classList.toggle('hidden', !useResourceList)
      }
    }
    if (listWrapper) {
      listWrapper.classList.toggle('hidden', !useList)
      listWrapper.style.display = useList ? '' : 'none'
    }
    if (enumInput) {
      enumInput.disabled = !useEnum
      if (enumInput.tomselect) {
        if (useEnum) {
          enumInput.tomselect.enable()
        } else {
          enumInput.tomselect.disable()
        }
      }
      if (useEnum) {
        this.populateEnumOptions(enumInput, enumValues, {
          includeBlank: valueType !== 'boolean' && operatorSet !== 'value_only',
        })
        if (existingValue) {
          this.setSelectValue(enumInput, existingValue)
        }
      } else if (enumInput.tomselect) {
        enumInput.tomselect.clear(true)
        enumInput.value = ''
        Array.from(enumInput.options).forEach(option => {
          option.selected = false
        })
      }
    }
    if (enumWrapper) {
      enumWrapper.classList.toggle('hidden', !useEnum)
      enumWrapper.style.display = useEnum ? '' : 'none'
    }

    this.applyDefaultValue(config, defaultValue)
    this.syncValueInputNames(textInput, enumInput, listInput, tagListInput, {
      useText: useText || isStatic,
      useEnum,
      useList,
    })
  }

  updateResourceListInput(listInput, resourceListType) {
    if (!listInput || !resourceListType) return

    const tom = listInput.tomselect

    if (resourceListType === 'collections' || resourceListType === 'countries') {
      listInput.setAttribute('data-resource-url', `/${resourceListType}`)
      listInput.removeAttribute('data-resource-filters')
      if (tom) {
        tom.settings.create = false
        tom.settings.persist = false
      }
      return
    }

    listInput.setAttribute('data-resource-url', '/tags')
    const fieldValue = this.fieldInput()?.value?.trim()
    const taggableType = this.taggableTypeForField(fieldValue)
    listInput.setAttribute('data-resource-filters', JSON.stringify({ taggable_type: taggableType }))
    if (tom) {
      tom.settings.create = true
      tom.settings.persist = true
    }
  }

  taggableTypeForField(fieldValue) {
    if (fieldValue === 'customer_tags.tags') {
      return 'OriginCustomer'
    }

    return 'Variant'
  }

  applyDefaultValue(config, defaultValue) {
    if (config.operatorSet === 'static') return

    if (config.valueType === 'enum' || config.valueType === 'boolean') {
      const enumInput = this.hasEnumInputTarget ? this.enumInputTarget : null
      if (!enumInput || enumInput.disabled) return
      const fallbackDefault = Array.isArray(config.enumValues) ? config.enumValues[0] : undefined
      const resolvedDefault = defaultValue ?? fallbackDefault
      if (resolvedDefault === undefined || resolvedDefault === null || resolvedDefault === '') return
      if (!enumInput.value) {
        this.setSelectValue(enumInput, resolvedDefault)
      }
      return
    }

    if (defaultValue === undefined || defaultValue === null) return

    if (config.valueType === 'list') {
      const listInput = this.activeListInput(config)
      if (!listInput || listInput.disabled) return
      const existing = Array.from(listInput.selectedOptions || [])
        .map(opt => opt.value)
        .filter(Boolean)
      if (existing.length > 0) return

      const values = Array.isArray(defaultValue)
        ? defaultValue
        : String(defaultValue)
            .split(',')
            .map(value => value.trim())
            .filter(Boolean)
      if (values.length === 0) return

      values.forEach(value => {
        const option = new Option(value, value, true, true)
        listInput.append(option)
      })
      listInput.dispatchEvent(new Event('change', { bubbles: true }))
      return
    }

    const textInput = this.hasTextInputTarget ? this.textInputTarget : null
    if (!textInput || textInput.disabled) return
    if (textInput.value) return
    textInput.value = defaultValue
  }

  populateEnumOptions(enumInput, enumValues = [], { includeBlank = true } = {}) {
    const values = Array.isArray(enumValues) ? enumValues : []
    const key = `${includeBlank ? 'with-blank' : 'no-blank'}:${values.join('|')}`
    if (enumInput.dataset.enumKey === key) return

    const tom = enumInput.tomselect
    if (tom) {
      const currentValue = tom.getValue() || enumInput.value
      tom.clearOptions()
      if (includeBlank) {
        tom.addOption({ value: '', text: 'Select value' })
      }
      values.forEach(value => {
        tom.addOption({
          value,
          text: value.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
        })
      })
      const nextValue = values.includes(currentValue) ? currentValue : includeBlank ? '' : values[0] || ''
      tom.setValue(nextValue, true)
      tom.refreshOptions(false)
      enumInput.dataset.enumKey = key
      return
    }

    const currentValue = enumInput.value
    enumInput.innerHTML = ''
    if (includeBlank) {
      enumInput.append(new Option('Select value', ''))
    }
    values.forEach(value => {
      const optionLabel = value.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
      enumInput.append(new Option(optionLabel, value, false, value === currentValue))
    })
    if (!includeBlank && !values.includes(enumInput.value) && values.length > 0) {
      enumInput.value = values[0]
    }
    enumInput.dataset.enumKey = key
    enumInput.dispatchEvent(new Event('change', { bubbles: true }))
  }

  fieldConfig() {
    const fieldSelect = this.fieldInput()
    const selectedOption = fieldSelect?.selectedOptions?.[0] || fieldSelect?.options?.[fieldSelect?.selectedIndex]
    if (!selectedOption) {
      return {}
    }

    const valueFromSelect = fieldSelect?.value?.trim()
    const tomOptionData =
      valueFromSelect && fieldSelect?.tomselect ? fieldSelect.tomselect.options?.[valueFromSelect] : null

    const valueType =
      selectedOption.dataset.valueType || selectedOption.getAttribute('data-value-type') || tomOptionData?.valueType
    const operatorSet =
      selectedOption.dataset.operatorSet ||
      selectedOption.getAttribute('data-operator-set') ||
      tomOptionData?.operatorSet
    const unit = selectedOption.dataset.unit || selectedOption.getAttribute('data-unit') || tomOptionData?.unit
    const unitPosition =
      selectedOption.dataset.unitPosition ||
      selectedOption.getAttribute('data-unit-position') ||
      tomOptionData?.unitPosition
    const enumValuesRaw =
      selectedOption.dataset.enumValues || selectedOption.getAttribute('data-enum-values') || tomOptionData?.enumValues
    const defaultOperator =
      selectedOption.dataset.defaultOperator ||
      selectedOption.getAttribute('data-default-operator') ||
      tomOptionData?.defaultOperator
    const defaultValue =
      selectedOption.dataset.defaultValue ||
      selectedOption.getAttribute('data-default-value') ||
      tomOptionData?.defaultValue
    const hint = selectedOption.dataset.hint || selectedOption.getAttribute('data-hint') || tomOptionData?.hint

    const enumValues = selectedOption.dataset.enumValues
      ? selectedOption.dataset.enumValues
          .split(',')
          .map(value => value.trim())
          .filter(Boolean)
      : []
    const resolvedEnumValues = enumValuesRaw
      ? enumValuesRaw
          .split(',')
          .map(value => value.trim())
          .filter(Boolean)
      : enumValues
    const defaultEnumValues = valueType === 'boolean' ? ['true', 'false'] : []

    const hintMap = {
      'visited_product.tags': 'Enter product tags (comma-separated).',
      'visited_product.collections': 'Select one or more product collections.',
      'products_purchased.tags': 'Enter purchased product tags (comma-separated).',
      'cart_products.collections': 'Select one or more product collections.',
      'cart_products.productId': 'Enter Shopify product IDs (comma-separated).',
      'cart_products.sku': 'Enter product SKUs (comma-separated).',
    }
    const fieldHintMap = {
      'number_of_visits.visitCount':
        'A visit includes activity within a 24-hour window. After 24 hours of inactivity, a new visit starts.',
      // Search conditions need the storefront to report its search results to the widget. Automatic on
      // Shopify app-embed installs; other/custom storefronts feed it via the setSearchContext API.
      search_no_results:
        'Needs the storefront to report search results — automatic on Shopify app-embed installs, otherwise set up via setSearchContext.',
      'search_result_count.resultCount':
        'Needs the storefront to report search results — automatic on Shopify app-embed installs, otherwise set up via setSearchContext.',
    }
    const SEARCH_DOCS_URL =
      'https://api-docs.shippedsuite.com/docs/chat-widget-javascript-sdk#reporting-on-site-search'
    const fieldHintDocsMap = {
      search_no_results: SEARCH_DOCS_URL,
      'search_result_count.resultCount': SEARCH_DOCS_URL,
    }
    const tagsFromMetadata =
      selectedOption.dataset.tags === 'true' ||
      selectedOption.getAttribute('data-tags') === 'true' ||
      tomOptionData?.tags === true ||
      tomOptionData?.tags === 'true'
    const tagsFromFieldValue = Boolean(valueFromSelect?.endsWith('.tags'))
    const collectionsFromFieldValue = valueFromSelect?.endsWith('.collections')
    const countriesFromFieldValue =
      selectedOption.dataset.countries === 'true' ||
      selectedOption.getAttribute('data-countries') === 'true' ||
      tomOptionData?.countries === true ||
      tomOptionData?.countries === 'true' ||
      Boolean(valueFromSelect?.endsWith('.country'))
    const tags = tagsFromMetadata || tagsFromFieldValue
    // Countries reuse the resource-backed list input (like tags/collections), served by /countries.
    const resourceListType = countriesFromFieldValue
      ? 'countries'
      : collectionsFromFieldValue
        ? 'collections'
        : tags
          ? 'tags'
          : null

    return {
      valueType,
      operatorSet,
      tags,
      resourceListType,
      unit,
      unitPosition,
      enumValues: resolvedEnumValues.length > 0 ? resolvedEnumValues : defaultEnumValues,
      defaultOperator,
      defaultValue,
      hint: hint || hintMap[valueFromSelect],
      fieldHint: fieldHintMap[valueFromSelect],
      fieldHintDocsUrl: fieldHintDocsMap[valueFromSelect],
    }
  }

  operatorOptionsFor(operatorSet) {
    const operatorSets = {
      number: ['equals', 'greater_than', 'less_than'],
      text: ['contains', 'not_contains', 'equals', 'not_equals'],
      list: ['contains', 'in', 'not_in'],
      boolean: ['equals'],
      enum: ['equals'],
      value_only: ['equals'],
    }
    return operatorSets[operatorSet] || []
  }

  updateOperatorOptions(config) {
    if (this.element.dataset.operatorSyncing === '1') return
    const operatorInput = this.hasOperatorSelectTarget ? this.operatorSelectTarget : null
    const operatorSelectWrapper = this.hasOperatorSelectWrapperTarget ? this.operatorSelectWrapperTarget : null
    const staticLabel = this.hasStaticLabelTarget ? this.staticLabelTarget : null
    const tom = operatorInput?.tomselect

    if (!operatorInput) return

    const fieldValue = this.fieldInput()?.value?.trim()
    if (!fieldValue) {
      this.hideOperator(operatorSelectWrapper, staticLabel, operatorInput, tom)
      return
    }

    if (!config.operatorSet) {
      this.hideOperator(operatorSelectWrapper, staticLabel, operatorInput, tom)
      return
    }

    if (config.operatorSet === 'static') {
      if (operatorSelectWrapper) {
        operatorSelectWrapper.classList.add('hidden')
      }
      if (staticLabel) {
        staticLabel.classList.remove('hidden')
      }
      this.withOperatorSyncing(() => {
        if (tom) {
          tom.clearOptions()
          tom.addOption({ value: 'equals', text: 'Equals' })
          tom.setValue(config.defaultOperator || 'equals', true)
        } else {
          operatorInput.innerHTML = ''
          operatorInput.append(new Option('Equals', 'equals', true, true))
          operatorInput.value = config.defaultOperator || 'equals'
        }
      })
      return
    }

    if (operatorSelectWrapper) {
      operatorSelectWrapper.classList.remove('hidden')
    }
    if (staticLabel) {
      staticLabel.classList.add('hidden')
    }

    const allowed = this.operatorOptionsFor(config.operatorSet)
    const previousValue = operatorInput.value
    this.withOperatorSyncing(() => {
      if (tom) {
        tom.clearOptions()
        allowed.forEach(operator => {
          tom.addOption({
            value: operator,
            text: this.operatorLabel(operator, config.operatorSet),
          })
        })
        const defaultOperator = config.defaultOperator || allowed[0] || ''
        const nextValue = allowed.includes(previousValue) ? previousValue : defaultOperator
        if (tom.getValue() !== nextValue) {
          tom.setValue(nextValue || '', true)
        }
        tom.refreshOptions(false)
      } else {
        operatorInput.innerHTML = ''
        allowed.forEach(operator => {
          operatorInput.append(new Option(this.operatorLabel(operator, config.operatorSet), operator))
        })

        const defaultOperator = config.defaultOperator || allowed[0] || ''
        const nextValue = allowed.includes(previousValue) ? previousValue : defaultOperator
        if (operatorInput.value !== nextValue) {
          operatorInput.value = nextValue
          operatorInput.dispatchEvent(new Event('change', { bubbles: true }))
        }
      }
    })
  }

  hideOperator(operatorSelectWrapper, staticLabel, operatorInput, tom) {
    if (operatorSelectWrapper) {
      operatorSelectWrapper.classList.add('hidden')
    }
    if (staticLabel) {
      staticLabel.classList.add('hidden')
    }
    if (tom) {
      tom.clearOptions()
      tom.setValue('', true)
    } else if (operatorInput) {
      operatorInput.innerHTML = ''
      operatorInput.value = ''
    }
  }

  operatorLabel(operator, operatorSet) {
    switch (operator) {
      case 'equals':
        return 'Equals'
      case 'contains':
        return operatorSet === 'list' ? 'Contains any' : 'Contains'
      case 'not_contains':
        return 'Does not contain'
      case 'greater_than':
        return 'Greater than'
      case 'less_than':
        return 'Less than'
      case 'in':
        return 'Contains all'
      case 'not_in':
        return 'Not contains'
      case 'not_equals':
        return 'Not equals'
      default:
        return operator
    }
  }

  setSelectValue(select, value) {
    if (value === undefined || value === null || value === '') return
    const stringValue = String(value)
    const tom = select.tomselect
    if (tom) {
      if (!tom.options[stringValue]) {
        tom.addOption({ value: stringValue, text: stringValue })
      }
      tom.setValue(stringValue, true)
      return
    }

    const existing = Array.from(select.options).find(option => option.value === stringValue)
    if (!existing) {
      select.append(new Option(stringValue, stringValue, true, true))
    }
    select.value = stringValue
    select.dispatchEvent(new Event('change', { bubbles: true }))
  }

  fieldInput() {
    return this.hasFieldTarget ? this.fieldTarget : null
  }

  resetIfFieldChanged() {
    const field = this.fieldInput()?.value?.trim()
    if (!field) return false

    const lastField = this.element.dataset.conditionField
    if (!lastField) {
      this.element.dataset.conditionField = field
      return false
    }

    if (lastField === field) return false

    this.element.dataset.conditionField = field

    const operatorInput = this.hasOperatorSelectTarget ? this.operatorSelectTarget : null
    if (operatorInput) {
      operatorInput.tomselect?.clear(true)
    }

    const textInput = this.hasTextInputTarget ? this.textInputTarget : null
    if (textInput) {
      textInput.value = ''
    }

    const listInput = this.hasListInputTarget ? this.listInputTarget : null
    if (listInput) {
      listInput.tomselect?.clear(true)
    }
    const tagListInput = this.hasTagListInputTarget ? this.tagListInputTarget : null
    if (tagListInput) {
      tagListInput.tomselect?.clear(true)
    }

    const enumInput = this.hasEnumInputTarget ? this.enumInputTarget : null
    if (enumInput) {
      enumInput.tomselect?.clear(true)
    }

    return true
  }

  normalizedValue() {
    const tagListInput = this.hasTagListInputTarget ? this.tagListInputTarget : null
    if (tagListInput && !tagListInput.disabled) {
      const selected = Array.from(tagListInput.selectedOptions || [])
        .map(opt => opt.value)
        .filter(Boolean)
      if (selected.length > 0) return selected
    }

    const listInput = this.hasListInputTarget ? this.listInputTarget : null
    if (listInput && !listInput.disabled) {
      const selected = Array.from(listInput.selectedOptions || [])
        .map(opt => opt.value)
        .filter(Boolean)
      if (selected.length > 0) return selected
    }

    const enumInput = this.hasEnumInputTarget ? this.enumInputTarget : null
    if (enumInput && !enumInput.disabled) {
      const value = enumInput.value?.toString().trim()
      return value || undefined
    }

    const textInput = this.hasTextInputTarget ? this.textInputTarget : null
    return textInput?.value?.toString().trim()
  }

  syncValueInputNames(textInput, enumInput, listInput, tagListInput, { useText, useEnum, useList }) {
    const operatorInput = this.hasOperatorSelectTarget ? this.operatorSelectTarget : null
    const baseName = operatorInput?.name?.replace(/\[operator\]$/, '[value]')

    if (textInput) {
      const name = baseName || textInput.dataset.originalName || textInput.name
      textInput.name = useText ? name : ''
    }
    if (enumInput) {
      const name = baseName || enumInput.dataset.originalName || enumInput.name
      enumInput.name = useEnum ? name : ''
    }
    if (listInput) {
      const name = baseName ? `${baseName}[]` : listInput.dataset.originalName || listInput.name
      listInput.name = useList && !listInput.disabled ? name : ''
    }
    if (tagListInput) {
      const name = baseName ? `${baseName}[]` : tagListInput.dataset.originalName || tagListInput.name
      tagListInput.name = useList && !tagListInput.disabled ? name : ''
    }
  }

  activeListInput(config = this.fieldConfig()) {
    if (config.resourceListType && this.hasTagListInputTarget) {
      return this.tagListInputTarget
    }

    return this.hasListInputTarget ? this.listInputTarget : null
  }

  setListValues(input, values, enabled) {
    if (!input) return

    input.disabled = !enabled

    if (input.tomselect) {
      if (enabled) {
        input.tomselect.enable()
        input.tomselect.setValue(values, true)
      } else {
        input.tomselect.clear(true)
        input.tomselect.disable()
      }
    }

    Array.from(input.options).forEach(option => {
      option.selected = enabled && values.includes(option.value)
    })
  }

  withOperatorSyncing(callback) {
    this.element.dataset.operatorSyncing = '1'
    try {
      callback()
    } finally {
      setTimeout(() => {
        this.element.dataset.operatorSyncing = ''
      }, 0)
    }
  }
}
